import { Theme } from '@mui/material/styles';

export type ValueOf<T> = T[keyof T];
export type ContributionsType = {
  contributionId: number;
  pensionableSalaryPaid: number;
  employeeContributions: number;
  ataFees: number;
  totalOperationalDaysYear: string;
  totalOperationalDaysMonth: string;
  effectiveDateOfRecord: string;
  statusCode: string;
  specialCode: string;
  startOfSchoolYear: string;
  recordType: string;
  fteDaysPaid: string;
  fteDaysMissed: string;
  fte: string;
  ftMonthlyRateOfSalary: number;
  ptMonthlyRateOfSalary: number;
  multipleContractId: string;
  contributionPayoutType: string;
  signingBonusAmount: string;
  allowanceAmount: number;
  allowancePayoutType: string;
  sin: string;
  warnings: { [key in keyof ContributionsType]?: string }[];
  overridesId: number | null;
  overriddenById: number | null;
};

export const EMPLOYEE_STATUS_LABEL_ACTIVE = 'Active';
export const EMPLOYEE_STATUS_LABEL_TERMINATED = 'Terminated';
export const EMPLOYEE_STATUS_LABEL_DECEASED = 'Deceased';
export type ValidEmployeeStatuses = 'D' | 'T' | '';
export type ValidEmployeeStatusLabels =
  | typeof EMPLOYEE_STATUS_LABEL_DECEASED
  | typeof EMPLOYEE_STATUS_LABEL_TERMINATED
  | typeof EMPLOYEE_STATUS_LABEL_ACTIVE;
export const EMPLOYEE_DISPLAY_LABELS_BY_STATUS: Record<
  ValidEmployeeStatuses,
  ValidEmployeeStatusLabels
> = {
  T: EMPLOYEE_STATUS_LABEL_TERMINATED,
  D: EMPLOYEE_STATUS_LABEL_DECEASED,
  '': EMPLOYEE_STATUS_LABEL_ACTIVE,
};

export type EmployeeType = {
  sin: string;
  employeeId: number;
  contributions: ContributionsType[];
  firstName: string;
  lastName: string;
  employerEmployeeNum: string;
  employmentMonth: string;
  submissionStatus: EmployeeSubmissionStatus;
  dateOfBirth: string;
  emailAddress: string;
  streetAddress: string;
  streetAddressLine2: string;
  city: string;
  province: string;
  postalCode: string;
  workPhone: string;
  phoneNum: string;
  ataLocalNum: string;
  schoolCode: string;
  teachingCertificateNum: string;
  priorStatusFullTime: '' | '1';
  terminationDate: null | string;
  terminationStatus: ValidEmployeeStatuses;
  totalOperationalDaysMonth: number;
  totalOperationalDaysYear: number;
  payrollSystemUpdateComment: string;
};

export type EmployerType = {
  employerName: string;
  boardNum: number;
  employerId: number;
  employerVersion: number;
  streetAddress: string;
  city: string;
  province: string;
  postalCode: string;
  mailingStreetAddress: string;
  mailingCity: string;
  mailingProvince: string;
  mailingPostalCode: string;
  phoneNum: string;
  faxNum: string;
  contactName: string;
  contactPhone: string;
  contactEmailAddress: string;
  employmentMonth: string;
  totalEmployerContribution: number;
  employerPayoutType: string;
  pensionPlan: string;
  defaultAtalocalNum: string;
  streetAddressLine2: string;
  mailingStreetAddressLine2: string;
  mailingFaxNumber: string;
  mailingPhoneNumber: string;
  mailingEmail: string;
};

export type EmployeeByIdErrorType = {
  errorId: number;
  errorMessage: string;
};

export interface EmployeeByIdContributions extends ContributionsType {
  contributionErrors: EmployeeByIdErrorType[];
  preSubmitError: boolean;
  preSubmitErrors: ValidationErrorsObjectType[] | null;
}

type ValidationErrorsObjectType = {
  description: string;
  severity: 'Warning' | 'Error' | 'Critical';
};

export interface ExtendedEmployeeType extends EmployeeType {
  isFlagged: boolean;
}

const contributionsStatuses = ['Accepted', 'Unsubmitted', 'Submitted'] as const;
export const graphStatuses = [...contributionsStatuses, 'Warnings', 'Errors'] as const;

type GraphStatusSubmitted = 'Submitted';
export type GraphStatusTypes = (typeof graphStatuses)[number];
export type GraphMetaStatuses = 'Accepted' | 'Attention' | 'Empty' | 'None';
export type GraphStatusesWithoutSubmitted = Exclude<GraphStatusTypes, GraphStatusSubmitted>;
export type EmployeeSubmissionStatus = 'A' | 'S' | 'E' | '';

export type IndividualPercentShape = {
  status: GraphStatusesWithoutSubmitted;
  percent: number;
  count: number;
  order: number;
  largest: boolean;
};

export const contributionColors: Record<GraphStatusTypes, string> = {
  Accepted: 'success.main',
  Unsubmitted: 'secondary.main',
  Submitted: 'info.main',
  Warnings: 'warning.light',
  Errors: 'error.main',
};

export const contributionHexColors: {
  [k in keyof typeof contributionColors]: (t: Theme) => string;
} = {
  Accepted: (theme) => theme.palette.success.main,
  Unsubmitted: (theme) => theme.palette.secondary.main,
  Submitted: (theme) => theme.palette.info.main,
  Warnings: (theme) => theme.palette.warning.light,
  Errors: (theme) => theme.palette.error.main,
};

export const contributionColorNames: { [k in keyof typeof contributionColors]: string } = {
  Accepted: 'green',
  Unsubmitted: 'prussian blue',
  Submitted: 'blue',
  Warnings: 'yellow',
  Errors: 'red',
};

export interface FilterParamsType {
  key: string;
  value: string | number | boolean;
  operator: string;
}

export interface FilterType {
  where: FilterParamsType;
}

export type LatestBatchStatusType = 'U' | 'E' | 'A';
